import {
  DotsVerticalIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  DropdownMenu,
  Label,
  Link,
  Select,
  SkeletonLoader,
  Title,
} from '@yoast/ui-library';
import cx from 'classnames';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getGetAnalysisListQueryKey,
  getGetAnalysisQueryKey,
  getGetBrandQueryKey,
  useDeleteSingleAnalysis,
  useGetAnalysis,
  useGetAnalysisList,
  useGetBrand,
  useGetBrands,
  useGetCurrentUser,
} from '../../api/generated/insights';
import {
  AIProviderEnum,
  AnalysisStatus,
  ScanStatus,
} from '../../api/generated/insights.schemas';
import AlertFinishBrandSetup from '../../components/AlertFinishBrandSetup';
import createComponent from '../../components/CreateComponent';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import Divider from '../../components/Divider';
import AnalysisDateSelect from '../../components/UI/AnalysisDateSelect';
import {
  useBrandAnalysisData,
  useBrandAnalysisMutations,
} from '../../contexts';
import useScan from '../../hooks/useScan';
import { formatScheduledAnalysisDate } from '../../utils/timeUtils';

interface HeaderCardProps {
  className?: string;
}

const SelectDiv = createComponent`yst-flex-1 yst-space-y-2`;

const HeaderCard: React.FC<HeaderCardProps> = ({ className }) => {
  const { brandId, analysisId, model } = useBrandAnalysisData();
  const { setModel, setAnalysisId } = useBrandAnalysisMutations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: selectedScanData } = useScan();

  // GET User
  const { data: user, isLoading: isLoadingUser } = useGetCurrentUser({
    query: {
      select: ({ data }) => data?.data,
    },
  });
  const currentUserDisplayName = user?.displayName;

  // GET Brands
  const { data: brands, isLoading: isLoadingBrands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,
    },
  });

  // GET Analysis List
  const { data: analysisList, isLoading: isLoadingAnalysisList } =
    useGetAnalysisList(brandId, {
      query: {
        enabled: !!brandId,
        select: ({ data }) => data?.analyses,
      },
    });

  // GET Analysis Detail
  const { data: analysisData, isLoading: isLoadingAnalysis } = useGetAnalysis(
    brandId,
    analysisId,
    {
      query: {
        enabled: !!brandId && !!analysisId,
        select: ({ data }) => data,
      },
    },
  );

  const { data: brand } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  // GET Brand Queries
  // const { data: brandQueries, isLoading: isLoadingQueries } =
  //   useGetBrandQueries(brandId, {
  //     query: {
  //       enabled: !!brandId,
  //       select: ({ data }) => data,
  //     },
  //   });

  // DELETE Single Analysis
  const { mutate: deleteSingleAnalysis, isPending: isDeletingSingleAnalysis } =
    useDeleteSingleAnalysis({
      mutation: {
        onSuccess: async () => {
          // Clear all analysis-related cache data
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisListQueryKey(brandId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetBrandQueryKey(brandId),
            }),
          ]);

          // Simple navigation logic based on current analysis list
          const remainingAnalyses =
            analysisList?.filter((a) => String(a.id) !== String(analysisId)) ||
            [];

          if (remainingAnalyses.length > 0) {
            // Navigate to the most recent remaining analysis
            const mostRecent = remainingAnalyses.reduce((latest, current) => {
              const latestTime = latest.createdAt
                ? new Date(latest.createdAt).getTime()
                : 0;
              const currentTime = current.createdAt
                ? new Date(current.createdAt).getTime()
                : 0;
              return currentTime > latestTime ? current : latest;
            });

            setAnalysisId(mostRecent.id);
            navigate(`/brands/${brandId}/analysis/${mostRecent.id}`);
          } else {
            // No analyses left
            setAnalysisId('latest');
            navigate(`/brands/${brandId}`);
          }

          setShowDeleteModal(false);
        },
      },
    });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const currentBrand = brands?.find(
    (brand) => String(brand.id) === String(brandId),
  );

  // Helper function to get pretty labels for AI providers
  const getAIProviderLabel = (provider?: string): string => {
    switch (provider) {
      case 'openai':
        return 'OpenAI';
      case 'perplexity':
        return 'Perplexity';
      case 'anthropic':
        return 'Anthropic';
      default:
        return provider || 'Unknown';
    }
  };

  // reduce scans down to an array of ai model strings
  const models = analysisData?.analysis?.scans?.map((scan) => ({
    value: scan.aiProvider,
    label: getAIProviderLabel(scan.aiProvider),
  })) || [{ value: '', label: 'Default model' }];

  // Helper: get valid selected analysisId
  const validAnalysisId = analysisList?.some(
    (a) => String(a.id) === String(analysisId),
  )
    ? analysisId
    : analysisList && analysisList.length > 0
      ? analysisList[0].id
      : 'latest';

  const { data: brandData } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  const brandName = brandData?.name || 'your brand';
  const nextScheduledAnalysis = formatScheduledAnalysisDate(
    brandData?.nextScheduledAnalysis,
  );
  const status = analysisData?.analysis?.status;

  // --- LEGAL ALERT LOGIC ---
  // State to track whether the legal alert has been acknowledged
  const [showLegalAlert, setShowLegalAlert] = useState(false);

  // Check localStorage and status conditions to determine if alert should show
  React.useEffect(() => {
    const currentAnalysisId = analysisData?.analysis?.id;
    // Only show alert when analysis is completed and we have valid IDs
    if (!brandId || !currentAnalysisId || status !== AnalysisStatus.completed) {
      setShowLegalAlert(false);
      return;
    }
    const key = `yst_legal_alert_dismissed_${brandId}_${currentAnalysisId}`;
    setShowLegalAlert(window.localStorage.getItem(key) === null);
  }, [brandId, analysisData?.analysis?.id, status]);

  const handleCloseLegalAlert = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const currentAnalysisId = analysisData?.analysis?.id;
    if (!brandId || !currentAnalysisId) return;
    const key = `yst_legal_alert_dismissed_${brandId}_${currentAnalysisId}`;
    window.localStorage.setItem(key, 'true');
    setShowLegalAlert(false);
  };

  // Alert content for scheduled analysis
  const analysisScheduledAlertContent = (
    <div className="yst-max-w-xl yst-space-y-2">
      <p className="yst-font-medium">Your analysis is scheduled</p>
      <p>
        You've completed the setup for{' '}
        <b className="yst-font-medium">{brandName}</b>. The analysis is
        scheduled to run on{' '}
        <b className="yst-font-medium">{nextScheduledAnalysis}</b>. You'll see
        the results here once it's ready. We'll also notify you by email.
      </p>
    </div>
  );

  // Define alert configurations for each status
  const alertConfigMap: Record<
    string,
    { alertContent: React.ReactNode; alertVariant?: string }
  > = {
    scheduledFirstAnalysis: {
      alertContent: analysisScheduledAlertContent,
      alertVariant: 'info',
    },
    [AnalysisStatus.pending]: {
      alertContent: analysisScheduledAlertContent,
      alertVariant: 'info',
    },
    [AnalysisStatus.processing]: {
      alertContent: (
        <>
          You've completed the setup for {brandName}. The analysis is now
          processing. You'll see the results here once it's ready. We'll also
          notify you by email.
        </>
      ),
      alertVariant: 'info',
    },
    [AnalysisStatus.failed]: {
      alertContent: (
        <>
          Analysis failed. Try restarting the analysis or contact support if the
          issue persists.
        </>
      ),
      alertVariant: 'danger',
    },
    [AnalysisStatus.cancelled]: {
      alertContent: (
        <>Analysis was cancelled. You can restart the analysis when ready.</>
      ),
      alertVariant: 'warning',
    },
  };

  // Determine the alert key based on status and brand setup
  type AlertKey = keyof typeof alertConfigMap;
  const alertKey =
    !status && brand?.setupStatus === 'scheduledFirstAnalysis'
      ? 'scheduledFirstAnalysis'
      : status;

  // Get alert configuration with fallback
  const { alertContent, alertVariant } = alertConfigMap[
    alertKey as AlertKey
  ] ?? {
    alertContent: null,
    alertVariant: 'info',
  };

  // Check if we should show "no analyses" warning
  const shouldShowNoAnalysesWarning =
    !isLoadingAnalysisList &&
    brand?.setupStatus !== 'pendingQuerySelection' &&
    analysisList?.length === 0 &&
    brand?.nextScheduledAnalysis === null;

  // should display notice about brand group changes
  const showOutdatedScanNotice =
    // analysis should be completed
    status === AnalysisStatus.completed &&
    // data is available to show
    selectedScanData &&
    // scan data is either updating or completed
    ['updating', 'completed'].includes(selectedScanData.status) &&
    // scan is not up to date with brand groups
    !selectedScanData.isUpToDateWithBrandGroups;

  // rescanning for brand groups
  const isUpdatingForNewBrandGroups =
    // check for scan to be updating
    selectedScanData?.status === ScanStatus.updating &&
    // and outdated scan notice to be shown
    showOutdatedScanNotice === true;

  return (
    <>
      {/* Delete Brand Analysis Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={() => {
          if (brandId && analysisId) {
            deleteSingleAnalysis({ brandId, analysisId });
          }
        }}
        isLoading={isDeletingSingleAnalysis}
        title="Delete analysis?"
        description={
          <>
            <p>
              This will permanently delete all data associated with this
              analysis.
            </p>
            <p>This action cannot be undone.</p>
          </>
        }
        confirmText="Yes, delete analysis"
        cancelText="No, go back"
      />

      {/* Brand analysis card */}
      <Card className={cx('yst-relative yst-overflow-visible', className)}>
        <Card.Content className="yst-mt-0">
          {/* header */}
          <div className="yst-flex yst-flex-1 yst-flex-col yst-flex-wrap yst-items-start yst-justify-between yst-gap-4 md:yst-flex-row xl:yst-items-center">
            <div className="xl:yst-max-w-1/4">
              <div className="yst-flex yst-flex-col xl:yst-flex-row xl:yst-items-baseline xl:yst-gap-2">
                <Title>
                  <>
                    {isLoadingUser ? (
                      <SkeletonLoader className="yst-inline-block yst-h-8 yst-w-64" />
                    ) : (
                      <>
                        Hi
                        {currentUserDisplayName && ` ${currentUserDisplayName}`}
                        ,
                      </>
                    )}{' '}
                  </>
                </Title>
                <p className="yst-text-sm yst-font-normal yst-text-slate-600">
                  welcome to your brand analysis!
                </p>
              </div>
            </div>

            {/* actions */}
            <div className="yst-flex yst-flex-row yst-flex-wrap yst-items-center yst-justify-end yst-gap-4 yst-self-end md:yst-self-start">
              {/* <Button
                variant="tertiary"
                className="yst-gap-1.5 yst-text-red-600"
                onClick={() => setShowDeleteModal(true)}
                disabled={isLoadingAnalysisList || isLoadingBrands}
              >
                <XIcon /> Reset brand analysis
              </Button> */}

              <Button
                variant="primary"
                className="yst-gap-1.5"
                onClick={() => navigate('/setup')}
                disabled={isLoadingAnalysisList || isLoadingBrands}
              >
                <PlusIcon /> Analyze a different brand
              </Button>

              {/* TODO */}
              {/* <Button
                variant="secondary"
                className="yst-gap-1.5"
                disabled={
                  isLoadingAnalysisList ||
                  isLoadingBrands ||
                  (brands && brands?.length < 1)
                }
              >
                <SparklesIcon /> Run analysis again
              </Button> */}

              {/* TODO */}
              <div className="yst-relative">
                <DropdownMenu>
                  <DropdownMenu.IconTrigger
                    screenReaderTriggerLabel="Open brand list options"
                    disabled={
                      isLoadingAnalysisList ||
                      isLoadingBrands ||
                      (brands && brands?.length < 1)
                    }
                  >
                    <DotsVerticalIcon />
                  </DropdownMenu.IconTrigger>
                  <DropdownMenu.List className="yst-absolute yst-right-0 yst-top-full yst-z-40 yst-mt-2 yst-flex yst-w-64 yst-flex-col yst-justify-start">
                    {/* TODO */}
                    {/* <DropdownMenu.ButtonItem
                    variant="secondary"
                    onClick={() => {
                      // Navigate to settings or edit mode
                      console.log('Edit settings and run again...');
                      // You can implement navigation to settings here
                      // navigate('/setup');
                    }}
                    className="yst-flex yst-justify-start yst-gap-1.5"
                  >
                    <AdjustmentsIcon className="yst-h-4 yst-w-4" />
                    Edit settings and run again...
                  </DropdownMenu.ButtonItem> */}
                    <DropdownMenu.ButtonItem
                      onClick={() => setShowDeleteModal(true)}
                      disabled={
                        isLoadingAnalysisList ||
                        isLoadingBrands ||
                        analysisData?.analysis?.status !== 'completed' ||
                        !analysisList ||
                        analysisList.length === 0
                      }
                      className="yst-flex yst-justify-start yst-gap-1.5 yst-text-red-600 hover:yst-bg-red-50"
                    >
                      <TrashIcon className="yst-h-4 yst-w-4" />
                      Delete analysis
                    </DropdownMenu.ButtonItem>
                  </DropdownMenu.List>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <Divider />

          {/* inline form with three fields: - Brand name, - Results from (dropdown with ChatGPT selected by default), Analysis date (read only) */}
          <form
            className={
              // Mobile: flex-col, stretch items; md+: flex-row, center items
              'yst-flex yst-flex-col yst-items-stretch yst-gap-4 md:yst-flex-row md:yst-items-center'
            }
          >
            {/* BRAND */}
            {/* TODO: create a local custom component */}
            <SelectDiv>
              <Label htmlFor="brand-name">Brand</Label>
              <Select
                onChange={(newBrandId: string) => {
                  navigate(`/brands/${newBrandId}`);
                }}
                id="brand-name"
                value={brandId ? brandId : 'loading'}
                options={
                  brands && brands.length > 0
                    ? brands.map((brand) => ({
                        value: brand.id,
                        label: brand.name,
                      }))
                    : [{ value: 'loading', label: 'Loading...' }]
                }
                disabled={
                  isLoadingBrands || !brands || brands.length < 2 || !brandId
                }
              />
            </SelectDiv>

            {/* ANALYSIS ID - Calendar Picker */}
            <SelectDiv>
              <Label htmlFor="analysis-date">Analysis date</Label>
              <AnalysisDateSelect
                analyses={analysisList || []}
                selectedAnalysisId={validAnalysisId}
                onAnalysisSelect={(newAnalysisId) => {
                  navigate(
                    `/brands/${brandId}/analysis/${String(newAnalysisId)}`,
                  );
                }}
                isLoading={isLoadingAnalysisList}
                disabled={
                  isLoadingAnalysisList ||
                  !analysisList ||
                  analysisList.length < 1
                }
                id="analysis-date"
              />
            </SelectDiv>

            {/* AI MODEL - dependent on the current analysis */}
            <SelectDiv>
              <Label htmlFor="ai-tool">AI model</Label>
              <Select
                onChange={(newValue: string) => {
                  // Validate newValue against known providers before updating state
                  if (
                    Object.values(AIProviderEnum).includes(
                      newValue as unknown as AIProviderEnum,
                    )
                  ) {
                    setModel(newValue as AIProviderEnum);
                  } else {
                    // Log an error for unexpected values — avoid silently accepting invalid providers
                    // This helps catch issues from unexpected API responses or UI bugs.
                    console.error('Invalid AI provider selected:', newValue);
                  }
                }}
                id="ai-tool"
                value={model}
                options={
                  isLoadingAnalysis || isLoadingAnalysisList
                    ? [{ value: model, label: 'Loading...' }]
                    : models
                }
                disabled={
                  isLoadingAnalysis ||
                  isLoadingAnalysisList ||
                  // ai model list array
                  [{}].length < 2
                }
              />
            </SelectDiv>
          </form>
        </Card.Content>
      </Card>

      <div className="ystaii-alerts yst-space-y-4">
        {/* Legal alert */}
        {showLegalAlert && (
          <Alert>
            <div className="yst-flex yst-w-full">
              <div className="yst-max-w-xl yst-space-y-4">
                <p className="yst-mb-0">
                  Note: This content was generated by an AI model (see dropdown
                  above) and may contain errors. Please review before acting on
                  it.
                </p>
              </div>
              <div className="yst-ml-auto">
                <Link onClick={handleCloseLegalAlert} variant="default">
                  <XIcon />
                </Link>
              </div>
            </div>
          </Alert>
        )}

        {/* Analysis alert */}
        {status !== AnalysisStatus.completed && alertContent && (
          <Alert className="md:yst-col-span-2" variant={alertVariant}>
            {alertContent}
          </Alert>
        )}

        {/* Show an alert if the user has modified their brand groupings */}
        {showOutdatedScanNotice && (
          <Alert variant="warning">
            Note: This scan is not up to date with your current brand groups.
            {isUpdatingForNewBrandGroups ? (
              <>
                {' '}
                We're updating your sentiment analysis to reflect your new brand
                groups. This may take a few minutes.
              </>
            ) : (
              <> Newer scans will use your newly selected brand groupings.</>
            )}
          </Alert>
        )}
      </div>

      {/* No analyses warning - Show when there are no analyses available */}
      {shouldShowNoAnalysesWarning && (
        <Alert variant="info" className="yst-mb-6">
          <div className="yst-flex yst-items-start yst-gap-3">
            <div className="yst-flex-1">
              <h3 className="yst-mb-2 yst-font-medium yst-text-blue-800">
                No analyses scheduled
              </h3>
              <p className="yst-text-bluer-700 yst-mb-0">
                <span className="yst-font-medium yst-text-blue-900">
                  {currentBrand?.name}
                </span>{' '}
                has not been analyzed yet, and no analyses are scheduled because
                this brand is set to inactive in your{' '}
                <span
                  className="yst-cursor-pointer yst-font-medium yst-text-blue-900 yst-underline"
                  onClick={() => navigate('/settings')}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/settings');
                    }
                  }}
                >
                  Settings
                </span>
                .
              </p>
            </div>
          </div>
        </Alert>
      )}

      {currentBrand && (
        <AlertFinishBrandSetup key={currentBrand.id} brand={currentBrand} />
      )}
    </>
  );
};

export default HeaderCard;
